import { KeyboardEvent, useEffect, useId, useRef, useState } from 'react';

import { Plus, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import { useDebounce } from '@/hooks/useDebounce';

import { DOCUMENT_SEARCH_MAX_LENGTH } from '../feedbackLimits';
import { DocumentResultsPanel } from './DocumentResultsPanel';
import { RelatedDocumentChip } from './RelatedDocumentChip';
import { isDocumentsUnavailable, useSearchRepositoryDocuments } from './api';
import { RepositoryDocument } from './types';
import { MAX_RELATED_DOCUMENTS, useRelatedDocuments } from './useRelatedDocuments';

const SEARCH_DEBOUNCE_MS = 250;

type Related = ReturnType<typeof useRelatedDocuments>;

/** True while a related-documents search box is open; the dialog uses it to let Escape close the list first. */
export const isDocumentSearchExpanded = (): boolean =>
    document.activeElement?.getAttribute('role') === 'combobox' &&
    document.activeElement.getAttribute('aria-expanded') === 'true';

function FieldLabel({ inputId }: { inputId: string }) {
    return (
        <Label htmlFor={inputId} className="font-semibold">
            Related documents
            <span className="font-normal text-muted-foreground"> (optional)</span>
        </Label>
    );
}

export function RelatedDocumentsField({ related }: { related: Related }) {
    const baseId = useId();
    const inputId = `${baseId}-search`;
    const listId = `${baseId}-list`;
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);

    const debouncedQuery = useDebounce({ value: query, delay: SEARCH_DEBOUNCE_MS });
    const search = useSearchRepositoryDocuments(debouncedQuery);

    // Only keyboard moves scroll; hover also changes the active row and must not make the list jump.
    const keyboardMoveRef = useRef(false);
    // Escape unmounts the focused input, so focus returns to the button that replaces it.
    const restoreFocusRef = useRef(false);
    const addButtonRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (open || !restoreFocusRef.current) return;
        restoreFocusRef.current = false;
        addButtonRef.current?.focus();
    }, [open, related.isFull]);
    const resultCount = search.data?.results.length ?? 0;
    // Results can shrink under a moved selection, so every use reads the clamped index.
    const active = Math.min(activeIndex, Math.max(resultCount - 1, 0));
    const activeId = search.data?.results[active]
        ? `${baseId}-option-${search.data.results[active].id}`
        : undefined;
    useEffect(() => {
        if (!keyboardMoveRef.current) return;
        keyboardMoveRef.current = false;
        const element = activeId ? document.getElementById(activeId) : null;
        if (element && typeof element.scrollIntoView === 'function') {
            element.scrollIntoView({ block: 'nearest' });
        }
    }, [active, activeId]);

    if (isDocumentsUnavailable(search.error)) {
        return (
            <div className="space-y-2">
                <FieldLabel inputId={inputId} />
                <p className="text-sm text-muted-foreground">
                    Documents aren&apos;t available for your account.
                </p>
            </div>
        );
    }

    const isOpen = open && !related.isFull;
    const results = search.data?.results ?? [];
    const activeDocument: RepositoryDocument | undefined = results[active];
    // The previous page stays on screen while the new query loads; Enter must not pick from it.
    const resultsMatchQuery =
        !search.isPlaceholderData && query.trim() === debouncedQuery.trim();
    const optionId = (document: RepositoryDocument) =>
        `${baseId}-option-${document.id}`;

    const openSearch = () => {
        // The hook stays mounted and does not retry, so reopening retries a failed lookup.
        if (search.isError) void search.refetch();
        setQuery('');
        setActiveIndex(0);
        setOpen(true);
    };
    const closeSearch = () => {
        setOpen(false);
        setQuery('');
        setActiveIndex(0);
    };
    const pick = (document: RepositoryDocument) => {
        if (!related.isAdded(document)) related.add(document);
    };
    const changeQuery = (next: string) => {
        setQuery(next);
        setActiveIndex(0);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        const last = results.length - 1;
        const moveTo = (next: number) => {
            keyboardMoveRef.current = next !== active;
            setActiveIndex(next);
        };
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            moveTo(Math.min(active + 1, Math.max(last, 0)));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            moveTo(Math.max(active - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (activeDocument && resultsMatchQuery) pick(activeDocument);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            restoreFocusRef.current = true;
            closeSearch();
        } else if (event.key === 'Backspace' && !query && related.items.length) {
            related.remove(related.items[related.items.length - 1].document.id);
        }
    };

    return (
        <div className="space-y-2">
            <FieldLabel inputId={inputId} />
            <div className="relative">
                <div
                    className={cn(
                        'flex flex-wrap items-center gap-2 rounded-md border border-input bg-background p-2',
                        isOpen && 'border-primary ring-2 ring-primary/30',
                    )}
                >
                    {related.items.map((item) => (
                        <RelatedDocumentChip
                            key={item.document.id}
                            item={item}
                            onRemove={() => related.remove(item.document.id)}
                        />
                    ))}
                    {isOpen ? (
                        <div className="flex min-w-[200px] flex-1 items-center gap-2 pl-1">
                            <Search
                                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                                aria-hidden="true"
                            />
                            <input
                                id={inputId}
                                autoFocus
                                type="text"
                                role="combobox"
                                autoComplete="off"
                                aria-autocomplete="list"
                                aria-expanded="true"
                                aria-controls={results.length ? listId : undefined}
                                aria-activedescendant={
                                    activeDocument
                                        ? optionId(activeDocument)
                                        : undefined
                                }
                                placeholder="Search by reference or title"
                                value={query}
                                maxLength={DOCUMENT_SEARCH_MAX_LENGTH}
                                onChange={(event) => changeQuery(event.target.value)}
                                onKeyDown={handleKeyDown}
                                onBlur={closeSearch}
                                className="h-[30px] min-w-0 flex-1 bg-transparent text-sm outline-none"
                            />
                        </div>
                    ) : (
                        <Button
                            ref={addButtonRef}
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-[30px] gap-1 px-3 font-normal"
                            disabled={related.isFull}
                            onClick={openSearch}
                        >
                            <Plus aria-hidden="true" />
                            Add document
                        </Button>
                    )}
                </div>
                {isOpen && (
                    <DocumentResultsPanel
                        listId={listId}
                        query={debouncedQuery}
                        page={search.data}
                        failed={search.isError}
                        optionId={optionId}
                        activeIndex={active}
                        isAdded={related.isAdded}
                        onPick={pick}
                        onHover={setActiveIndex}
                    />
                )}
            </div>
            {related.isFull && (
                <p className="text-xs text-muted-foreground">
                    {`You can add up to ${MAX_RELATED_DOCUMENTS} documents.`}
                </p>
            )}
        </div>
    );
}
